/**
 * Клавиши поля ответа в переписке по помещению (владелец 30.10.2026: «Enter отправляет,
 * Ctrl+Enter — новая строка»; «иногда не вставляется то, что диктую и вставляю через Ctrl+V»;
 * «сделай вики тоже»). Чистые функции без React — покрыто `composer-keys.test.ts`.
 *
 * Копия CRM: `frontend/src/lib/composer-keys.ts` (разные пакеты, общего места нет) —
 * правила держать одинаковыми.
 */

/** Минимум от `KeyboardEvent`, нужный для разбора (в тестах — обычные объекты). */
export interface KeyLike {
  key: string;
  code?: string;
  keyCode?: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  /** Идёт набор через IME / системную диктовку (подчёркнутый текст ещё не принят). */
  isComposing?: boolean;
}

/**
 * Что делать с Enter в поле ответа:
 * - `send` — отправить (обычный Enter на компьютере);
 * - `newline` — вставить перенос строки самим (Ctrl/⌘/Alt+Enter: браузер сам строку не переносит);
 * - `native` — не мешать браузеру (Shift+Enter переносит сам; телефон; идёт IME/диктовка — Enter её принимает);
 * - `null` — это не Enter.
 */
export type EnterAction = 'send' | 'newline' | 'native' | null;

export function composerEnterAction(e: KeyLike, opts: { enterSends: boolean }): EnterAction {
  if (e.key !== 'Enter') return null;
  // 229 — Chrome/Safari присылают его, пока IME держит текст; Enter здесь принимает набор, а не отправляет.
  if (e.isComposing || e.keyCode === 229) return 'native';
  if (e.shiftKey) return 'native';
  if (e.ctrlKey || e.metaKey || e.altKey) return 'newline';
  // На телефоне у клавиатуры нет Shift+Enter — Enter переносит строку, отправка кнопкой.
  return opts.enterSends ? 'send' : 'native';
}

/** Mac / iPhone / iPad: там вставка — ⌘V, а Ctrl+V браузер не вставляет. */
export function isMacPlatform(nav: { platform?: string; userAgent?: string } | undefined): boolean {
  if (!nav) return false;
  return /Mac|iPhone|iPad|iPod/i.test(nav.platform || nav.userAgent || '');
}

/**
 * Ctrl+V на Mac: по привычке с Windows вставляют Ctrl, а браузер на Mac так не вставляет —
 * вставляем сами через Clipboard API. Клавишу берём по `code` (физическая V), иначе
 * в русской раскладке `key` = «м». На Windows/Linux — false: вставит сам браузер.
 */
export function isMacCtrlPaste(e: KeyLike, isMac: boolean): boolean {
  if (!isMac || !e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return false;
  return e.code === 'KeyV' || e.key === 'v' || e.key === 'V' || e.key === 'м' || e.key === 'М';
}

/** Сколько знаков принимает сервер в сообщении переписки (`@MaxLength(5000)` у `body`). */
export const MESSAGE_MAX_CHARS = 5000;

/**
 * Счётчик длины: показываем ближе к пределу, красный — за пределом (отправка не уйдёт).
 * Поле больше не режет вставку молча (`maxLength` браузера обрезал продиктованный текст
 * без предупреждения) — длинный текст виден целиком, сократить его решает человек.
 */
export function messageLengthState(length: number, max = MESSAGE_MAX_CHARS): { show: boolean; over: boolean; label: string } {
  const over = length > max;
  return { show: length >= max * 0.9, over, label: `${length.toLocaleString('ru-RU')} / ${max.toLocaleString('ru-RU')}` };
}
